/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { History } from 'history';
import { FiDownload } from 'react-icons/fi';

import { IAppState } from 'src/store';
import { Button, Icon, Table, Text, View } from 'src/components/common';
import './styles.scss';
import { PATHS } from 'src/appConfig/paths';
import { Deposition, FileData } from 'src/dataFactory/depositionFactory';
import Loading from 'src/components/Loading';
import { ErrorService, Toastify } from 'src/services';
import { depositionAction } from 'src/redux/depositionRedux';
import { filesAction } from 'src/redux/fileRedux';
import { MUIDataTableOptions } from 'mui-datatables';
import { columns, FileDataTableRow } from './helpers';
import { FilePreDownloadPayload } from 'src/redux/fileRedux/types';
import { getSortParam } from 'src/utils';

interface ParamTypes {
  id: string;
}

const FinalDepositionDetail: React.FC<Props> = ({
  history,
  onGetDepositionDetail,
  onGetDepositionFinal,
  onDownloadFiles,
}) => {
  const { id } = useParams<ParamTypes>();
  const [deposition, setDeposition] = useState<Deposition>();
  const [finalFiles, setFinalFiles] = useState<FileDataTableRow[]>([]);
  const [total, setTotal] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    if (id) {
      onGetDepositionDetail({ uri: id }, fetchDataSuccess, _error => {
        handleBackToPrevious();
        return;
      });
    } else handleBackToPrevious();
  }

  const fetchFilesData = (params: any) => {
    if (deposition) {
      const searchParams = {
        search: params.search ?? '',
        status: params.status ?? [],
        sort: params.sort ? getSortParam(params.sort) : 'LAST_MODIFIED',
        order: params.order ? params.order.toUpperCase() : 'DESC',
        skip: params.skip ?? '0',
        take: params.take ?? '10',
      };
      onGetDepositionFinal(
        { uri: deposition.subResources.finalDepositionFiles.uri, params: searchParams },
        fetchFinalSuccess,
        fetchDataFailed,
      );
    }
  };

  const fetchDataSuccess = (res: Deposition) => {
    setIsLoading(false);
    setDeposition(res);
    fetchFilesData(res);
  };

  const fetchFinalSuccess = (res: FileData[], total?: number) => {
    setFinalFiles(
      res.map<FileDataTableRow>(r => ({
        ...r,
        name: [r.name, r.extension],
        type: r.extension,
        lastModified: r.updatedDate,
      })),
    );
    setTotal(!!total ? total : res.length);
  };

  const fetchDataFailed = (error: any) => {
    setIsLoading(false);
    ErrorService.handler(error);
  };

  // ====================== Handlers ======================

  const handleDownloadFiles = () => {
    setIsLoading(true);
    const downloadFiles: FilePreDownloadPayload[] = finalFiles.map(file => ({
      downloadUri: file.downloadUri,
      fileName: `${file.name}.${file.extension}`,
    }));
    onDownloadFiles(downloadFiles);
  };

  const handleBackToPrevious = (message?: string) => {
    if (!!message) {
      Toastify.success(message);
    }
    history.push(PATHS.RepositoryManagement);
  };

  const tableOptions: MUIDataTableOptions = {
    count: total,
    filter: false,
  };

  return (
    <View className="ctn-depo-detail">
      {!!deposition?.id ? (
        <View className="container" style={{ width: '100%' }}>
          <Button
            variant="text"
            className={'back-previous-page mt-40'}
            icon={<Icon name={'ic_arrow_left'} />}
            onClick={() => {
              handleBackToPrevious();
            }}>
            MANAGE REPOSITORY
          </Button>
          <View className="mb-20" isRow justify="space-between" align="center">
            <Text size={36}>
              {deposition.caseId} - {deposition.name}
            </Text>
            <Button
              onClick={handleDownloadFiles}
              label="DOWNLOAD ALL"
              disabled={finalFiles.length === 0}
              icon={<FiDownload />}
              variant="secondary"
            />
          </View>
          <View className="mt-32">
            <Table
              title="File Explorer"
              data={finalFiles}
              tableOptions={tableOptions}
              columns={columns}
              onAction={fetchFilesData}
            />
          </View>
        </View>
      ) : (
        <View isRow justify="center" className="mt-72">
          <View isRow justify="center" className="mt-72">
            {isLoading && (
              <View className="ctn-loading">
                <Loading loadingStyle={2} variant="primary" />
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & { history: History };

const mapStateToProps = (state: IAppState) => ({
  depositionFiles: state.deposition.depositionFiles,
  currentUser: state.auth.user,
  isUploadDone: state.file.isUploadDone,
});

const mapDispatchToProps = {
  onGetDepositionDetail: depositionAction.getDepositionById,
  onGetDepositionFinal: depositionAction.getDepositionFinalFiles,
  onDownloadFiles: filesAction.downloadFiles,
};

export default connect(mapStateToProps, mapDispatchToProps)(FinalDepositionDetail);
